import { Inject, Injectable } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { POST_LOGOUT_REDIRECT_URI } from '../okta.config';
import { TokenService } from './token.service';

/**
 * Auth facade over the Okta SDK. Public API is unchanged so existing consumers
 * (AppService, AppComponent, guards) keep working; the implementation now uses
 * Authorization Code + PKCE instead of the old implicit flow.
 */
@Injectable({
    providedIn: 'root'
})
export class AuthService {

    constructor(
        @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
        private tokenService: TokenService
    ) {
    }

    isAuthorized(): boolean {
        return this.tokenService.hasAccessToken();
    }

    login(returnUrl?: string): void {
        const originalUri = returnUrl || (window.location.pathname + window.location.search);
        this.oktaAuth.setOriginalUri(originalUri);
        void this.oktaAuth.signInWithRedirect();
    }

    /**
     * Okta leg of the global logout chain, reached via the /signout route (SignoutGuard):
     *
     * header "Log out" → /logout → ingress 301 → ServiceNow logout → /signout → here (revoke the
     * tokens, clear them, redirect to the end-session endpoint) → POST_LOGOUT_REDIRECT_URI →
     * ingress 301 → Khoros community front-channel logout.
     *
     * The tokens live in the Okta SDK's own storage, so only the SDK can clean them up reliably —
     * which is why this runs in the SPA instead of the shared_web_assets logout page it replaced
     * (that page still read the pre-PKCE localStorage keys, so it silently sent an empty
     * id_token_hint and skipped the end-session leg altogether, leaving the SSO session alive).
     */
    async logout(): Promise<void> {
        // Best-effort revocation: a failing /revoke (network, CORS) must not abort the end-session
        // redirect below, or the Okta SSO session would survive the logout.
        await this.revokeQuietly(() => this.oktaAuth.revokeRefreshToken());
        await this.revokeQuietly(() => this.oktaAuth.revokeAccessToken());

        try {
            await this.oktaAuth.signOut({
                // Already handled above.
                revokeAccessToken: false,
                revokeRefreshToken: false,
                // Clear storage now rather than flagging the tokens for deferred removal: this flow
                // leaves the SPA for good, so the deferred cleanup would never run and the tokens
                // would outlive the logout in sessionStorage.
                clearTokensBeforeRedirect: true
            });
        } catch (err) {
            console.error('Okta sign-out failed', err);
            // Never strand the user on a blank /signout page: drop the local tokens and continue
            // the chain so the community session is terminated regardless.
            this.oktaAuth.tokenManager.clear();
            window.location.assign(POST_LOGOUT_REDIRECT_URI);
        }
    }

    // Runs one revocation call (e.g. () => oktaAuth.revokeAccessToken()), swallowing failures so
    // the caller can carry on with the logout. The SDK revoke calls already succeed silently when
    // the token is absent, so only a transport-level error reaches the catch.
    private async revokeQuietly(revoke: () => Promise<unknown>): Promise<void> {
        try {
            await revoke();
        } catch (err) {
            console.error('Token revocation failed during logout', err);
        }
    }
}
